import { useEffect, useState } from 'react';
import { RiAlertLine, RiFingerprintLine, RiLoader4Line } from 'react-icons/ri';
import { CaseFilePanel } from './components/CaseFilePanel';
import { CanvasStage } from './components/CanvasStage';
import { RightPanel } from './components/RightPanel';
import { CaseDialog } from './components/CaseDialog';
import { ShareDialog } from './components/ShareDialog';
import { MembersDialog } from './components/MembersDialog';
import { UsersDialog } from './components/UsersDialog';
import { BriefView } from './components/BriefView';
import { ProviderDialog } from './components/ProviderDialog';
import { ChangePasswordDialog } from './components/ChangePasswordDialog';
import { ShareView } from './components/ShareView';
import { LoginScreen } from './components/LoginScreen';
import { Toaster } from './components/Toaster';
import { TopBar } from './components/TopBar';
import { Button } from './components/ui/button';
import { TooltipProvider } from './components/ui/tooltip';
import { useStore } from './state/store';
import { useAuthStore } from './state/authStore';

const FEATURES = [
  '关联墙：无限画布关系编排（自研引擎，红线绳 / 图钉 / 圈注 / 手绘 / 印章）',
  '线索台账：对象、线索、关系、证据链与事件时间线',
  '公开信息检索：网页、DNS、注册信息、文本线索抽取',
  '研判与操作规划：AI 产出可执行动作，人工确认后批量应用',
  '授权门 + 案件归属隔离 + 全量审计，默认不对外暴露',
];

export default function App() {
  const authStatus = useAuthStore(state => state.status);
  const bootstrapAuth = useAuthStore(state => state.bootstrap);
  const exitShare = useAuthStore(state => state.exitShare);
  const shareToken = useAuthStore(state => state.shareToken);
  const shareSnapshot = useAuthStore(state => state.shareSnapshot);
  const shareLoading = useAuthStore(state => state.shareLoading);
  const shareError = useAuthStore(state => state.shareError);

  const bootstrap = useStore(state => state.bootstrap);
  const booted = useStore(state => state.booted);
  const loading = useStore(state => state.loading);
  const engagements = useStore(state => state.engagements);
  const activeEngagementId = useStore(state => state.activeEngagementId);
  const panels = useStore(state => state.panels);
  const preview = useStore(state => state.preview);
  const [caseDialogOpen, setCaseDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [providersOpen, setProvidersOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  useEffect(() => {
    void bootstrapAuth();
  }, [bootstrapAuth]);

  useEffect(() => {
    if (authStatus === 'authed') void bootstrap();
  }, [authStatus, bootstrap]);

  useEffect(() => {
    if (booted && engagements.length === 0 && authStatus === 'authed' && !shareSnapshot) {
      setCaseDialogOpen(true);
    }
  }, [booted, engagements.length, authStatus, shareSnapshot]);

  // 全屏预览：同步浏览器全屏状态
  useEffect(() => {
    const el = document.documentElement;
    if (preview) {
      if (!document.fullscreenElement) void el.requestFullscreen?.().catch(() => undefined);
    } else if (document.fullscreenElement) {
      void document.exitFullscreen?.().catch(() => undefined);
    }
  }, [preview]);

  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement && useStore.getState().preview) {
        useStore.getState().setPreview(false);
      }
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // 视图快捷键
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const store = useStore.getState();

      if (event.key === 'Escape') {
        if (store.preview) store.setPreview(false);
        return;
      }
      if (event.key === '[') store.togglePanel('left');
      else if (event.key === ']') store.togglePanel('right');
      else if (event.key === '\\') store.focusWall();
      else if (event.shiftKey && event.key.toLowerCase() === 'f') store.setPreview(!store.preview);
      else if (event.shiftKey && event.key.toLowerCase() === 'h') {
        const allOn = Object.values(store.hud).every(Boolean);
        store.setAllHud(!allOn);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  /* ── 只读共享演示 ─────────────────────────────────── */
  if (shareToken && shareLoading) {
    return (
      <Centered>
        <RiLoader4Line className="size-5 animate-spin text-muted-foreground" />
        <p className="text-[12px] text-muted-foreground">正在加载共享演示…</p>
      </Centered>
    );
  }

  if (shareToken && shareError && !shareSnapshot) {
    return (
      <Centered>
        <RiAlertLine className="size-6 text-[var(--color-cord)]" />
        <p className="text-[13px] font-medium">无法打开共享演示</p>
        <p className="max-w-[380px] text-center text-[11.5px] text-muted-foreground">{shareError}</p>
        <Button variant="outline" onClick={exitShare}>
          返回登录
        </Button>
      </Centered>
    );
  }

  if (shareSnapshot) {
    return (
      <TooltipProvider delayDuration={300}>
        <ShareView />
        <Toaster />
      </TooltipProvider>
    );
  }

  /* ── 身份门 ───────────────────────────────────────── */
  if (authStatus === 'loading') {
    return (
      <Centered>
        <RiLoader4Line className="size-5 animate-spin text-muted-foreground" />
        <p className="text-[12px] text-muted-foreground">正在校验身份…</p>
      </Centered>
    );
  }

  if (authStatus !== 'authed') {
    return (
      <TooltipProvider delayDuration={300}>
        <LoginScreen />
        <Toaster />
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className={`app-shell grid h-full ${preview ? 'grid-rows-[1fr]' : 'grid-rows-[48px_1fr]'}`}>
        {!preview && (
          <TopBar
            onNewCase={() => setCaseDialogOpen(true)}
            onShare={() => setShareDialogOpen(true)}
            onMembers={() => setMembersOpen(true)}
            onUsers={() => setUsersOpen(true)}
            onProviders={() => setProvidersOpen(true)}
            onChangePassword={() => setPasswordOpen(true)}
          />
        )}

        <main className="flex min-h-0 overflow-hidden">
          {!booted && loading && (
            <div className="grid flex-1 place-items-center text-[12px] text-muted-foreground">
              正在载入案件数据…
            </div>
          )}

          {booted && !activeEngagementId && (
            <div className="grid flex-1 place-items-center p-6">
              <div className="w-full max-w-[560px] rounded-[var(--radius-lg)] border border-border bg-card p-7 shadow-[0_20px_60px_rgba(27,28,30,0.08)]">
                <div className="mb-4 flex items-center gap-3">
                  <div className="grid size-10 place-items-center rounded-[var(--radius)] bg-primary text-primary-foreground">
                    <RiFingerprintLine className="size-5" />
                  </div>
                  <div>
                    <h1 className="text-[16px] font-semibold tracking-[0.1em]">EXPOTAR</h1>
                    <p className="text-[11.5px] text-muted-foreground">授权社工目标分析与情报关联工作台</p>
                  </div>
                </div>
                <ul className="flex flex-col gap-2">
                  {FEATURES.map(item => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-[12px] leading-relaxed text-muted-foreground"
                    >
                      <span className="mt-[7px] size-1.5 shrink-0 rounded-full bg-primary/50" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button className="mt-5 w-full" onClick={() => setCaseDialogOpen(true)}>
                  立案：新建授权委托
                </Button>
              </div>
            </div>
          )}

          {booted && activeEngagementId && (
            <>
              {!preview && panels.left && <CaseFilePanel />}
              <CanvasStage />
              {!preview && panels.right && <RightPanel />}
            </>
          )}
        </main>

        <CaseDialog open={caseDialogOpen} onClose={() => setCaseDialogOpen(false)} />
        <ShareDialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} />
        <MembersDialog open={membersOpen} onClose={() => setMembersOpen(false)} />
        <UsersDialog open={usersOpen} onClose={() => setUsersOpen(false)} />
        <ProviderDialog open={providersOpen} onClose={() => setProvidersOpen(false)} />
        <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
        <Toaster />

        <BriefView />

        {preview && (
          <button
            type="button"
            onClick={() => useStore.getState().setPreview(false)}
            className="fixed right-3 top-3 z-[60] rounded-full border border-border bg-card/70 px-3 py-1.5 text-[10.5px] text-muted-foreground opacity-0 backdrop-blur transition-opacity hover:opacity-100 focus:opacity-100"
          >
            退出预览（Esc）
          </button>
        )}
      </div>
    </TooltipProvider>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 bg-background">{children}</div>
  );
}